/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IRefinerItemData, RefinerItemType } from '@msdyn365-commerce/components';
import { ChannelIdentity, SalesStatus, BusinessPartner, BusinessPartnerUser } from '@msdyn365-commerce/retail-proxy';
import { IOrderHistoryWithSearchAndFiltersResources } from '../order-history-with-search-and-filters.props.autogenerated';
import { getOrderStatusLabel } from './get-sales-order';
import { DateRefinerOptionItem, IDateRange, OrderStatusRefinerOptionItem, RefinerSourceType } from './refiner-option';

export function mapChannelIdentityIntoIRefinerItemData(channelIdentity?: ChannelIdentity): IRefinerItemData {
    return {
        sourceTypeValue: RefinerSourceType.Origin,
        label: channelIdentity?.Name ?? channelIdentity?.RecordId.toString() ?? '',
        value: channelIdentity?.RecordId.toString(),
        refinerItemId: channelIdentity?.RecordId ?? 0,
        isChecked: false,
        itemType: RefinerItemType.Check
    };
}

export function mapDateRefinerOptionIntoIRefinerItemData(
    dateRefinerOptionItem: DateRefinerOptionItem,
    resources: IOrderHistoryWithSearchAndFiltersResources
): IRefinerItemData {
    let itemData: IRefinerItemData;
    switch (dateRefinerOptionItem) {
        case DateRefinerOptionItem.LastSevenDays:
            itemData = {
                label: resources.lastSevenDaysCreatedDateRefinerItemLabel,
                itemType: RefinerItemType.Check
            } as IRefinerItemData;
            break;
        case DateRefinerOptionItem.LastThirtyDays:
            itemData = {
                label: resources.lastThirtyDaysCreatedDateRefinerItemLabel,
                itemType: RefinerItemType.Check
            } as IRefinerItemData;
            break;
        case DateRefinerOptionItem.LastThreeMonths:
            itemData = {
                label: resources.lastThreeMonthsCreatedDateRefinerItemLabel,
                itemType: RefinerItemType.Check
            } as IRefinerItemData;
            break;
        case DateRefinerOptionItem.LastSixMonths:
            itemData = {
                label: resources.lastSixMonthsCreatedDateRefinerItemLabel,
                itemType: RefinerItemType.Check
            } as IRefinerItemData;
            break;
        case DateRefinerOptionItem.Customized:
            itemData = {
                label: resources.customizedCreatedDateRefinerItemLabel,
                minLabel: resources.customizedStartDateRefinerItemLabel,
                maxLabel: resources.customizedEndDateRefinerItemLabel,
                itemType: RefinerItemType.DateRange
            } as IRefinerItemData;
            break;
        default:
            throw new Error(`Invalid date refiner option: ${dateRefinerOptionItem}`);
    }

    return {
        ...itemData,
        sourceTypeValue: RefinerSourceType.CreatedDate,
        value: dateRefinerOptionItem.toString(),
        refinerItemId: dateRefinerOptionItem,
        isChecked: false
    } as IRefinerItemData;
}

export function mapIRefinerItemDataIntoDateRefinerOptionItem(refinerItemData?: IRefinerItemData): DateRefinerOptionItem | undefined {
    if (!refinerItemData) {
        return;
    }

    const value = Number(refinerItemData.value);
    if (isNaN(value)) {
        return;
    }

    switch (value) {
        case DateRefinerOptionItem.LastSevenDays:
        case DateRefinerOptionItem.LastThirtyDays:
        case DateRefinerOptionItem.LastThreeMonths:
        case DateRefinerOptionItem.LastSixMonths:
        case DateRefinerOptionItem.Customized:
            return value;
        default:
            return;
    }
}

export function getDateRange(dateRefinerOptionItem: DateRefinerOptionItem): IDateRange {
    switch (dateRefinerOptionItem) {
        case DateRefinerOptionItem.LastSevenDays:
            return {
                startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
                endDate: new Date()
            } as IDateRange;
        case DateRefinerOptionItem.LastThirtyDays:
            return {
                startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
                endDate: new Date()
            } as IDateRange;
        case DateRefinerOptionItem.LastThreeMonths:
            return {
                startDate: new Date(Date.now() - 3 * 30 * 24 * 60 * 60 * 1000),
                endDate: new Date()
            } as IDateRange;
        case DateRefinerOptionItem.LastSixMonths:
            return {
                startDate: new Date(Date.now() - 6 * 30 * 24 * 60 * 60 * 1000),
                endDate: new Date()
            } as IDateRange;
        default:
            return {} as IDateRange;
    }
}

export function getDateFromString(dateString?: string): Date | undefined {
    if (!dateString) {
        return undefined;
    }

    const parts: string[] = dateString.split('-');
    if (parts.length !== 3) {
        return undefined;
    }

    const now = new Date();
    return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]), now.getHours(), now.getMinutes(), now.getSeconds());
}

export function mapOrderStatusRefinerOptionIntoIRefinerItemData(
    orderStatusRefinerOptionItem: OrderStatusRefinerOptionItem,
    resources: IOrderHistoryWithSearchAndFiltersResources
): IRefinerItemData {
    let itemData: IRefinerItemData;
    switch (orderStatusRefinerOptionItem) {
        case OrderStatusRefinerOptionItem.Created:
        case OrderStatusRefinerOptionItem.Delivered:
        case OrderStatusRefinerOptionItem.Invoiced:
        case OrderStatusRefinerOptionItem.Canceled:
            itemData = {
                label: getOrderStatusRefinerOptionItemLabel(orderStatusRefinerOptionItem, resources)
            } as IRefinerItemData;
            break;
        default:
            throw new Error(`Invalid order status refiner option: ${orderStatusRefinerOptionItem}`);
    }

    return {
        ...itemData,
        sourceTypeValue: RefinerSourceType.OrderStatus,
        value: orderStatusRefinerOptionItem.toString(),
        refinerItemId: orderStatusRefinerOptionItem,
        isChecked: false,
        itemType: RefinerItemType.Check
    } as IRefinerItemData;
}

function getOrderStatusRefinerOptionItemLabel(
    orderStatusRefinerOptionItem: OrderStatusRefinerOptionItem,
    resources: IOrderHistoryWithSearchAndFiltersResources
): string {
    return getOrderStatusLabel(mapOrderStatusRefinerOptionItemIntoSalesStatus(orderStatusRefinerOptionItem), resources);
}

export function mapIRefinerItemDataIntoOrderStatusRefinerOptionItem(
    refinerItemData?: IRefinerItemData
): OrderStatusRefinerOptionItem | undefined {
    if (!refinerItemData) {
        return;
    }

    const value = Number(refinerItemData.value);
    if (isNaN(value)) {
        return;
    }

    switch (value) {
        case OrderStatusRefinerOptionItem.Created:
        case OrderStatusRefinerOptionItem.Delivered:
        case OrderStatusRefinerOptionItem.Invoiced:
        case OrderStatusRefinerOptionItem.Canceled:
            return value as OrderStatusRefinerOptionItem;
        default:
            return;
    }
}

export function mapOrderStatusRefinerOptionItemIntoSalesStatus(
    orderStatusRefinerOptionItem?: OrderStatusRefinerOptionItem
): SalesStatus | undefined {
    switch (orderStatusRefinerOptionItem) {
        case OrderStatusRefinerOptionItem.Created:
            return SalesStatus.Created;
        case OrderStatusRefinerOptionItem.Delivered:
            return SalesStatus.Delivered;
        case OrderStatusRefinerOptionItem.Invoiced:
            return SalesStatus.Invoiced;
        case OrderStatusRefinerOptionItem.Canceled:
            return SalesStatus.Canceled;
        default:
            return;
    }
}

export function mapOrganizationIntoIRefinerItemData(org?: BusinessPartner): IRefinerItemData {
    const refinerId = Number(org?.BusinessPartnerId) || 0;
    const organizationCustomerAccountNumber = org?.OrganizationCustomerAccountNumber ?? '';
    const name = org?.Name ?? '';
    return {
        sourceTypeValue: RefinerSourceType.Organization,
        label: name || organizationCustomerAccountNumber,
        value: organizationCustomerAccountNumber,
        refinerItemId: refinerId,
        isChecked: false,
        itemType: RefinerItemType.Check
    };
}

export function mapContactIntoIRefinerItemData(contact?: BusinessPartnerUser): IRefinerItemData {
    const refinerId = Number(contact?.PartyRecordId) || 0;
    const contactPersonName = `${contact?.FirstName ?? ''} ${contact?.LastName ?? ''}`;
    const contactPersonId = contact?.ContactPersonId ?? '';
    return {
        sourceTypeValue: RefinerSourceType.Contact,
        label: contactPersonName,
        value: contactPersonId,
        refinerItemId: refinerId,
        isChecked: false,
        itemType: RefinerItemType.Check
    };
}

/**
 * Maps a group of BusinessPartnerUser entries (same person, multiple orgs) into a single IRefinerItemData.
 * Groups by PartyRecordId to deduplicate display names, storing all ContactPersonIds as comma-separated value.
 * @param contacts - Array of BusinessPartnerUser entries sharing the same PartyRecordId.
 * @returns IRefinerItemData with all ContactPersonIds in the value field.
 */
export function mapContactGroupIntoIRefinerItemData(contacts: BusinessPartnerUser[]): IRefinerItemData {
    const first = contacts[0];
    const refinerId = Number(first?.PartyRecordId) || 0;
    const contactPersonName = `${first?.FirstName ?? ''} ${first?.LastName ?? ''}`;
    const contactPersonIds = contacts
        .map(c => c.ContactPersonId)
        .filter((id): id is string => !!id)
        .join(',');
    return {
        sourceTypeValue: RefinerSourceType.Contact,
        label: contactPersonName,
        value: contactPersonIds,
        refinerItemId: refinerId,
        isChecked: false,
        itemType: RefinerItemType.Check
    };
}

/**
 * Groups BusinessPartnerUser entries by PartyRecordId and returns deduplicated refiner items.
 * @param contacts - Array of BusinessPartnerUser entries from GetAccessibleUsers or GetUsers API.
 * @returns Array of IRefinerItemData, one per unique PartyRecordId.
 */
export function mapContactsIntoDeduplicatedRefinerItems(contacts: BusinessPartnerUser[]): IRefinerItemData[] {
    const groupedByPartyRecordId = new Map<number, BusinessPartnerUser[]>();
    for (const contact of contacts) {
        const partyRecordId = contact.PartyRecordId ?? 0;
        if (partyRecordId === 0) {
            continue;
        }
        const group = groupedByPartyRecordId.get(partyRecordId) ?? [];
        group.push(contact);
        groupedByPartyRecordId.set(partyRecordId, group);
    }
    return Array.from(groupedByPartyRecordId.values()).map(mapContactGroupIntoIRefinerItemData);
}
